import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { searchUsers, getOrCreateConversation } from '../config/chat';
import { useAppContext } from '../context/AppContext';
import {
  UserData,
  Conversation,
  ConversationWithUser,
  Message,
} from '../types';

export const useChat = () => {
  const { user } = useAppContext();

  const [conversations, setConversations] = useState<ConversationWithUser[]>(
    []
  );
  const [selectedConversation, setSelectedConversation] =
    useState<ConversationWithUser | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [searchResults, setSearchResults] = useState<UserData[] | null>(null);
  const [loadingConversations, setLoadingConversations] = useState(true);

  // Listen to conversations
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'conversations'),
      where('members', 'array-contains', user.uid),
      orderBy('updatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const convos: ConversationWithUser[] = [];

        for (const docSnap of snapshot.docs) {
          const data = { ...docSnap.data(), id: docSnap.id } as Conversation;
          const otherUserId = data.members.find((id) => id !== user.uid);

          if (otherUserId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', otherUserId));
              if (userDoc.exists()) {
                convos.push({
                  ...data,
                  otherUser: userDoc.data() as UserData,
                });
              }
            } catch {
              // Skip conversations where user data can't be fetched
            }
          }
        }

        setConversations(convos);
        setLoadingConversations(false);
      },
      () => setLoadingConversations(false)
    );

    return () => unsubscribe();
  }, [user]);

  // Keep selectedConversation synced
  useEffect(() => {
    if (!selectedConversation) return;
    const updated = conversations.find(
      (c) => c.id === selectedConversation.id
    );
    if (updated) setSelectedConversation(updated);
  }, [conversations, selectedConversation]);

  // Listen to messages for selected conversation
  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      return;
    }

    const q = query(
      collection(db, 'conversations', selectedConversation.id, 'messages'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(
        snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as Message[]
      );
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?.id]);

  const handleSearch = useCallback(
    async (term: string) => {
      if (!term.trim()) {
        setSearchResults(null);
        return;
      }
      if (!user) return;
      try {
        setSearchResults(await searchUsers(term, user.uid));
      } catch {
        setSearchResults([]);
      }
    },
    [user]
  );

  const handleSelectUser = useCallback(
    async (otherUser: UserData) => {
      if (!user) return;
      setSearchResults(null);

      const conversationId = await getOrCreateConversation(
        user.uid,
        otherUser.id
      );

      const existing = conversations.find((c) => c.id === conversationId);
      setSelectedConversation(
        existing ??
          ({
            id: conversationId,
            members: [user.uid, otherUser.id].sort(),
            lastMessage: null,
            updatedAt: null,
            createdAt: null,
            otherUser,
          } as unknown as ConversationWithUser)
      );
    },
    [user, conversations]
  );

  const handleSelectConversation = useCallback(
    (conversation: ConversationWithUser) => {
      setSelectedConversation(conversation);
      setSearchResults(null);
    },
    []
  );

  return {
    user,
    conversations,
    selectedConversation,
    messages,
    searchResults,
    loadingConversations,
    handleSearch,
    handleSelectUser,
    handleSelectConversation,
  };
};
